/**
 * @file pointLevels.ts
 * @brief Sub-points (DES 12-10-2026): one level of indent under a note's points.
 *
 * @details
 * `point_levels` rides beside `points`: 0 = a point, 1 = a sub-point of the
 * point above. One level only, and the first point is never a sub-point.
 * null (or a map that does not line up) means every line is a point, so a
 * stale map can never indent the wrong line.
 *
 * Labels: points are numbered 1, 2, 3; the sub-points under each are lettered
 * a, b, c, restarting at every point.
 *
 * @references
 * - FRONTEND/src/screens/Notes/notesStorage.ts (pointLabels, mapPointLevels)
 * - BACKEND/src/features/notes/routes.js (pointLevels on POST / and PATCH)
 *
 * @author DES
 * @date 12-10-2026
 */

/**
 * Levels from the API, kept only when they are an array the same length as
 * the points. Index 0 is always 0; any value >= 1 is 1. All-flat reads as null.
 */
export function readPointLevels(raw: unknown, count: number): number[] | null {
  if (!Array.isArray(raw) || raw.length !== count || count === 0) {
    return null;
  }
  const out = raw.map((v, i) => (i > 0 && Number(v) >= 1 ? 1 : 0));
  return out.some((v) => v === 1) ? out : null;
}

/** "1", "2", "a", "b", "3"... one label per point, aligned with `levels`. */
export function pointLabels(levels: readonly number[] | null | undefined, count: number): string[] {
  let n = 0;
  let sub = 0;
  return Array.from({ length: count }, (_, i) => {
    if ((levels?.[i] ?? 0) > 0 && n > 0) {
      sub += 1;
      return String.fromCharCode(96 + Math.min(sub, 26));
    }
    n += 1;
    sub = 0;
    return String(n);
  });
}

/** Levels for the rows an editor saves: blank rows dropped, the first forced to 0. */
export function filledRowLevels(rows: readonly { text: string; level: number }[]): number[] {
  return rows
    .filter((r) => r.text.trim())
    .map((r, i) => (i === 0 ? 0 : r.level > 0 ? 1 : 0));
}

/**
 * Points as copy/share text: "1. Budget", sub-points indented four spaces with
 * their letter ("    a. Q3").
 */
export function formatPointsText(points: readonly string[], levels: readonly number[] | null | undefined): string {
  const labels = pointLabels(levels, points.length);
  return points
    .map((p, i) => (/^\d+$/.test(labels[i]) ? `${labels[i]}. ${p}` : `    ${labels[i]}. ${p}`))
    .join("\n");
}
